import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { State } from '../redux/state';
import { withLocalization, LProps } from '../utils/wrappers';
import { CompetitionUserScore, UserRead } from '../userApi';
import { Link } from 'react-router-dom';
import * as selectors from '../redux/selectors';
import { makeUserPath, makeEntityPath } from '../utils/routerHelper';
import { displayFullName } from '../utils/userApiHelper';
import { CompetitionPledges, Entity, Competition } from '../userApi';
import { estimateEntityDollarSavings } from '../utils/actionSavings';
import { campusLabelFor } from '../utils/competitionLabels';

interface ManualUserScoreProps {
  score: CompetitionUserScore;
  rank: number;
  showRank: boolean;
  showEntityMetrics: boolean;
  showDepartment?: boolean;
}

interface UserScoreProps extends ManualUserScoreProps {
  user: UserRead;
  competitionPledges: CompetitionPledges;
  showDollarSavings: boolean;
  showDepartment: boolean;
  department?: Entity;
  competitionID: number;
}

const UserScore = ({
  rank, score, user, formatNumber, showRank, showEntityMetrics, showDollarSavings,
  competitionPledges, showDepartment, department, competitionID, i18n
}: LProps<UserScoreProps>) => {
  if (!user) return null;
  const waterGallons = Number(score.savingsWaterOz || 0);
  const dollarsSaved = showDollarSavings
    ? estimateEntityDollarSavings({ savingsCO2Lbs: score.savingsCO2Lbs || 0 }, competitionPledges)
    : 0;
  const scoreboardT = i18n.getFixedT(i18n.language, 'scoreboard');
  return (
  <tr>
    {showRank && <td>#{rank}</td>}
    <td>
      <Link to={makeUserPath(score.competitionID, user, scoreboardT)}>{displayFullName(user)}</Link>
    </td>
    {showDepartment && (
      <td className="cc__scoreboard__department">
        {department
          ? <Link to={makeEntityPath(competitionID, department, scoreboardT)}>{department.name}</Link>
          : null}
      </td>
    )}
    {showEntityMetrics && (
      <td className="cc__scoreboard__percentage">{formatNumber(score.pledgeCount || 0, 0)}</td>
    )}
    {showEntityMetrics && (
      <td className="cc__scoreboard__points">{formatNumber(score.savingsCO2Lbs || 0, 0)}</td>
    )}
    {showEntityMetrics && showDollarSavings && (
      <td className="cc__scoreboard__points">{formatNumber(waterGallons, 0)}</td>
    )}
    {showEntityMetrics && showDollarSavings && (
      <td className="cc__scoreboard__points">${formatNumber(Math.round(dollarsSaved), 0)}</td>
    )}
    <td className="cc__scoreboard__points">{formatNumber(score.points, 0)}</td>
  </tr>
  );
};

const ConnectedUserScore = compose(
  withLocalization('scoreboard'),
  connect((state: State, ownProps: ManualUserScoreProps & { showDepartment?: boolean }) => {
    const competitionName = String((state.competition && state.competition.name) || '').toLowerCase();
    return {
      user: selectors.someUser(state, ownProps.score.userID),
      competitionPledges: state.competitionPledges,
      showDollarSavings: competitionName === 'ccsf' || competitionName === 'sf' || competitionName === 'sfenvironment',
      showDepartment: Boolean(ownProps.showDepartment),
      department: ownProps.showDepartment
        ? selectors.acceptedUniversity(state, ownProps.score.userID)
        : undefined,
      competitionID: state.competition && state.competition.id
    };
  })
)(UserScore) as React.SFC<ManualUserScoreProps>;

interface ManualProps {
  userScores: CompetitionUserScore[];
  showRank?: boolean;
  viewAll?: string;
  /** Division entity page: show actions, CO2e, water, $ next to points. */
  showEntityMetrics?: boolean;
  /** Show department (campus / university entity) column on participant rows. */
  showDepartment?: boolean;
  compact?: boolean;
  competition?: Competition;
}

const ScoreboardParticipants = ({
  t, userScores, showRank = true, viewAll = '', showEntityMetrics = false, showDepartment = false,
  compact = false, competition
}: LProps<ManualProps>) => {
  const showDollarCols = showEntityMetrics;
  const departmentHeader = campusLabelFor(competition, t('Campus'));
  return (
  <div>
    {viewAll && <Link to={viewAll}>{t('View all >')}</Link>}
    <div className="cc__scoreboard__table-wrap">
    <table className={compact || showEntityMetrics ? 'cc__scoreboard__table--compact' : undefined}>
      <thead>
        <tr>
          {showRank && <th />}
          <th>{showEntityMetrics ? t('Participants') : ''}</th>
          {showDepartment && !showEntityMetrics && (
            <th className="cc__scoreboard__department">{departmentHeader}</th>
          )}
          {showEntityMetrics && <th className="cc__scoreboard__percentage">{t('Actions')}</th>}
          {showEntityMetrics && (
            <th
              className="cc__scoreboard__points"
              dangerouslySetInnerHTML={{ __html: t('Lbs CO<sub>2</sub>e') }}
            />
          )}
          {showEntityMetrics && showDollarCols && (
            <th className="cc__scoreboard__points" title={t('Water saved (gallons)')}>{t('Water (gal)')}</th>
          )}
          {showEntityMetrics && showDollarCols && (
            <th className="cc__scoreboard__points" title={t('Estimated San Francisco bill savings')}>{t('$ Saved')}</th>
          )}
          <th className="cc__scoreboard__points">{t('Points')}</th>
        </tr>
      </thead>
      <tbody>
        {(userScores ? userScores.map((userScore, i) => (
          <ConnectedUserScore
            key={userScore.userID}
            score={userScore}
            rank={i + 1}
            showRank={showRank}
            showEntityMetrics={showEntityMetrics}
            showDepartment={showDepartment}
          />
        )) : null)}
      </tbody>
    </table>
    </div>
  </div>
  );
};

export default compose(
  withLocalization('scoreboard')
)(ScoreboardParticipants) as React.SFC<ManualProps>;
